import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { UserCheck, UserX, Clock, Users, RefreshCw, Search, Trash2, CheckSquare, XSquare } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

export const ApprovalManagement = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [approvedUsers, setApprovedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  
  // Selection state
  const [selectedPending, setSelectedPending] = useState([]);
  const [selectedApproved, setSelectedApproved] = useState([]);
  
  // Dialog state
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showBulkRejectDialog, setShowBulkRejectDialog] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchPendingUsers = useCallback(async () => {
    try {
      const response = await api.get('/users/pending-approval');
      setPendingUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch pending users');
    }
  }, []);

  const fetchApprovedUsers = useCallback(async () => {
    try {
      const response = await api.get('/users');
      // Filter to only approved non-admin users
      const approved = response.data.filter(u => 
        u.approval_status === 'approved' && u.role !== 'Admin'
      );
      setApprovedUsers(approved);
    } catch (error) {
      toast.error('Failed to fetch approved users');
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchPendingUsers(), fetchApprovedUsers()]);
    setLoading(false);
  }, [fetchPendingUsers, fetchApprovedUsers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Single user actions
  const handleApprove = async (userId, userName) => {
    setActionLoading(true);
    try {
      await api.post(`/users/${userId}/approve`);
      toast.success(`${userName} has been approved!`);
      await fetchData();
      setSelectedPending([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve user');
    } finally {
      setActionLoading(false);
    }
  };

  const openRejectDialog = (user) => {
    setSelectedUser(user);
    setRejectReason('');
    setShowRejectDialog(true);
  };

  const handleReject = async () => {
    if (!selectedUser) return;
    
    setActionLoading(true);
    try {
      await api.post(`/users/${selectedUser.id}/reject?reason=${encodeURIComponent(rejectReason)}`);
      toast.success(`${selectedUser.first_name} ${selectedUser.last_name} has been rejected`);
      await fetchData();
      setShowRejectDialog(false);
      setSelectedUser(null);
      setSelectedPending([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject user');
    } finally {
      setActionLoading(false);
    }
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    
    setActionLoading(true);
    try {
      await api.delete(`/users/${selectedUser.id}`);
      toast.success(`${selectedUser.first_name} ${selectedUser.last_name} has been deleted`);
      await fetchData();
      setShowDeleteDialog(false);
      setSelectedUser(null);
      setSelectedApproved([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setActionLoading(false);
    }
  };

  // Bulk actions
  const handleBulkApprove = async () => {
    if (selectedPending.length === 0) return;
    
    setActionLoading(true);
    let successCount = 0;
    let errorCount = 0;
    
    for (const userId of selectedPending) {
      try {
        await api.post(`/users/${userId}/approve`);
        successCount++;
      } catch (error) {
        errorCount++;
      }
    }
    
    if (successCount > 0) {
      toast.success(`${successCount} user(s) approved successfully`);
    }
    if (errorCount > 0) {
      toast.error(`Failed to approve ${errorCount} user(s)`);
    }
    
    await fetchData();
    setSelectedPending([]);
    setActionLoading(false);
  };

  const handleBulkReject = async () => {
    if (selectedPending.length === 0) return;
    
    setActionLoading(true);
    let successCount = 0;
    let errorCount = 0;
    
    for (const userId of selectedPending) {
      try {
        await api.post(`/users/${userId}/reject?reason=${encodeURIComponent(rejectReason)}`);
        successCount++;
      } catch (error) {
        errorCount++;
      }
    }
    
    if (successCount > 0) {
      toast.success(`${successCount} user(s) rejected successfully`);
    }
    if (errorCount > 0) {
      toast.error(`Failed to reject ${errorCount} user(s)`);
    }
    
    await fetchData();
    setSelectedPending([]);
    setShowBulkRejectDialog(false);
    setRejectReason('');
    setActionLoading(false);
  };

  const handleBulkDelete = async () => {
    if (selectedApproved.length === 0) return;
    
    setActionLoading(true);
    let successCount = 0;
    let errorCount = 0;
    
    for (const userId of selectedApproved) {
      try {
        await api.delete(`/users/${userId}`);
        successCount++;
      } catch (error) {
        errorCount++;
      }
    }
    
    if (successCount > 0) {
      toast.success(`${successCount} user(s) deleted successfully`);
    }
    if (errorCount > 0) {
      toast.error(`Failed to delete ${errorCount} user(s)`);
    }
    
    await fetchData();
    setSelectedApproved([]);
    setShowBulkDeleteDialog(false);
    setActionLoading(false);
  };

  // Selection handlers
  const togglePendingSelection = (userId) => {
    setSelectedPending(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleApprovedSelection = (userId) => {
    setSelectedApproved(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAllPending = () => {
    if (selectedPending.length === filteredPending.length) {
      setSelectedPending([]);
    } else {
      setSelectedPending(filteredPending.map(u => u.id));
    }
  };

  const selectAllApproved = () => {
    if (selectedApproved.length === filteredApproved.length) {
      setSelectedApproved([]);
    } else {
      setSelectedApproved(filteredApproved.map(u => u.id));
    }
  };

  // Filtering
  const filteredPending = pendingUsers.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.first_name.toLowerCase().includes(searchLower) ||
      user.last_name.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower) ||
      (user.leader_name && user.leader_name.toLowerCase().includes(searchLower))
    );
  });

  const filteredApproved = approvedUsers.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.first_name.toLowerCase().includes(searchLower) ||
      user.last_name.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower)
    );
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="approval-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#333333]">User Management</h1>
          <p className="text-gray-600">Approve registrations and manage team members</p>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchData}
          disabled={loading}
          data-testid="refresh-button"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{pendingUsers.length}</p>
              <p className="text-sm text-gray-600">Pending Approvals</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <UserCheck className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{approvedUsers.length}</p>
              <p className="text-sm text-gray-600">Approved Members</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{pendingUsers.length + approvedUsers.length + 1}</p>
              <p className="text-sm text-gray-600">Total Team Members</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Search */}
      <Card className="p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by name, email, or leader..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Pending ({pendingUsers.length})
          </TabsTrigger>
          <TabsTrigger value="approved" className="flex items-center gap-2">
            <UserCheck className="h-4 w-4" />
            Approved ({approvedUsers.length})
          </TabsTrigger>
        </TabsList>

        {/* Pending Users Tab */}
        <TabsContent value="pending">
          {/* Bulk Actions Bar */}
          {selectedPending.length > 0 && (
            <Card className="p-3 mb-4 bg-blue-50 border-blue-200">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-blue-800">
                  {selectedPending.length} user(s) selected
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    onClick={handleBulkApprove}
                    disabled={actionLoading}
                    data-testid="bulk-approve-button"
                  >
                    <CheckSquare className="h-4 w-4 mr-1" />
                    Approve All
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    onClick={() => setShowBulkRejectDialog(true)}
                    disabled={actionLoading}
                    data-testid="bulk-reject-button"
                  >
                    <XSquare className="h-4 w-4 mr-1" />
                    Reject All
                  </Button>
                </div>
              </div>
            </Card>
          )}

          <Card className="overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <RefreshCw className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">Loading pending users...</p>
              </div>
            ) : filteredPending.length === 0 ? (
              <div className="p-8 text-center">
                <UserCheck className="h-12 w-12 text-green-400 mx-auto mb-2" />
                <p className="text-gray-500 font-medium">No pending approvals</p>
                <p className="text-sm text-gray-400">All user registrations have been processed</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox 
                        checked={selectedPending.length === filteredPending.length && filteredPending.length > 0}
                        onCheckedChange={selectAllPending}
                        data-testid="select-all-pending"
                      />
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Leader</TableHead>
                    <TableHead>Registered</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPending.map((user) => (
                    <TableRow key={user.id} data-testid={`pending-user-${user.id}`}>
                      <TableCell>
                        <Checkbox 
                          checked={selectedPending.includes(user.id)}
                          onCheckedChange={() => togglePendingSelection(user.id)}
                          data-testid={`select-pending-${user.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        {user.first_name} {user.last_name}
                      </TableCell>
                      <TableCell className="text-gray-600">{user.email}</TableCell>
                      <TableCell>
                        {user.leader_name ? (
                          <Badge variant="outline">{user.leader_name}</Badge>
                        ) : (
                          <span className="text-gray-400">Direct to Admin</span>
                        )}
                      </TableCell>
                      <TableCell className="text-gray-500 text-sm">
                        {formatDate(user.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleApprove(user.id, `${user.first_name} ${user.last_name}`)}
                            disabled={actionLoading}
                            data-testid={`approve-${user.id}`}
                          >
                            <UserCheck className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => openRejectDialog(user)}
                            disabled={actionLoading}
                            data-testid={`reject-${user.id}`}
                          >
                            <UserX className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        {/* Approved Users Tab */}
        <TabsContent value="approved">
          {/* Bulk Actions Bar */}
          {selectedApproved.length > 0 && (
            <Card className="p-3 mb-4 bg-red-50 border-red-200">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-red-800">
                  {selectedApproved.length} user(s) selected
                </p>
                <Button
                  size="sm"
                  className="bg-red-600 hover:bg-red-700"
                  onClick={() => setShowBulkDeleteDialog(true)}
                  disabled={actionLoading}
                  data-testid="bulk-delete-button"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete Selected
                </Button>
              </div>
            </Card>
          )}

          <Card className="overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <RefreshCw className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">Loading approved users...</p>
              </div>
            ) : filteredApproved.length === 0 ? (
              <div className="p-8 text-center">
                <Users className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 font-medium">No approved members yet</p>
                <p className="text-sm text-gray-400">Approve pending registrations to add team members</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox 
                        checked={selectedApproved.length === filteredApproved.length && filteredApproved.length > 0}
                        onCheckedChange={selectAllApproved}
                        data-testid="select-all-approved"
                      />
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredApproved.map((user) => (
                    <TableRow key={user.id} data-testid={`approved-user-${user.id}`}>
                      <TableCell>
                        <Checkbox 
                          checked={selectedApproved.includes(user.id)}
                          onCheckedChange={() => toggleApprovedSelection(user.id)}
                          data-testid={`select-approved-${user.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        {user.first_name} {user.last_name}
                      </TableCell>
                      <TableCell className="text-gray-600">{user.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{user.role.replace(/_/g, ' ')}</Badge>
                      </TableCell>
                      <TableCell className="text-gray-500 text-sm">
                        {formatDate(user.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          onClick={() => openDeleteDialog(user)}
                          disabled={actionLoading}
                          data-testid={`delete-${user.id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Single Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject User Registration</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-gray-600">
              Are you sure you want to reject <strong>{selectedUser?.first_name} {selectedUser?.last_name}</strong>&apos;s registration?
            </p>
            <div>
              <Label htmlFor="reason">Reason (optional)</Label>
              <Textarea
                id="reason"
                placeholder="Provide a reason for rejection..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="mt-1"
                data-testid="reject-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-red-600 hover:bg-red-700"
              onClick={handleReject}
              disabled={actionLoading}
              data-testid="confirm-reject-button"
            >
              {actionLoading ? 'Rejecting...' : 'Reject User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Single Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-gray-600">
              Are you sure you want to permanently delete <strong>{selectedUser?.first_name} {selectedUser?.last_name}</strong>&apos;s account? This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDelete}
              disabled={actionLoading}
              data-testid="confirm-delete-button"
            >
              {actionLoading ? 'Deleting...' : 'Delete User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Reject Dialog */}
      <Dialog open={showBulkRejectDialog} onOpenChange={setShowBulkRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Multiple Users</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-gray-600">
              Are you sure you want to reject <strong>{selectedPending.length}</strong> user registration(s)?
            </p>
            <div>
              <Label htmlFor="bulk-reason">Reason (optional, applies to all)</Label>
              <Textarea
                id="bulk-reason"
                placeholder="Provide a reason for rejection..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="mt-1"
                data-testid="bulk-reject-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkRejectDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-red-600 hover:bg-red-700"
              onClick={handleBulkReject}
              disabled={actionLoading}
              data-testid="confirm-bulk-reject-button"
            >
              {actionLoading ? 'Rejecting...' : `Reject ${selectedPending.length} User(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Multiple Accounts</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-gray-600">
              Are you sure you want to permanently delete <strong>{selectedApproved.length}</strong> user account(s)? This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkDeleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-red-600 hover:bg-red-700"
              onClick={handleBulkDelete}
              disabled={actionLoading}
              data-testid="confirm-bulk-delete-button"
            >
              {actionLoading ? 'Deleting...' : `Delete ${selectedApproved.length} Account(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
